import React, { useEffect, useState, useRef } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';

const MyDirectory = ({ userData }) => {
  const [viewType, setViewType] = useState('submissions'); // 'assignments', 'submissions', 'projects', or 'projectSubmissions'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projectSubFilter, setProjectSubFilter] = useState('all'); // 'all' | 'needsEvaluation' | 'inProgress' | 'completed'
  const [assignmentSubFilter, setAssignmentSubFilter] = useState('all'); // 'all' | 'requireGrading' | 'survey' | 'graded'
  const hasSetDefaultFiltersRef = useRef(false);

  // Store all the fetched data
  const [myAssignments, setMyAssignments] = useState([]);
  const [assignmentSubmissions, setAssignmentSubmissions] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [projectSubmissions, setProjectSubmissions] = useState([]);
  const [evaluationComments, setEvaluationComments] = useState([]);
  const [flagComments, setFlagComments] = useState([]);
  
  const [counts, setCounts] = useState({
    assignments: 0,
    submissions: 0,
    submissionsRequiringGrading: 0,
    projects: 0,
    projectsWithUnresolvedFlags: 0,
    projectSubmissions: 0,
    projectSubmissionsWaitingForEvaluation: 0
  });
  
  // Store flagged projects and evaluation-required submissions for highlighting
  const [flaggedProjects, setFlaggedProjects] = useState(new Set());
  const [evaluationRequiredSubmissions, setEvaluationRequiredSubmissions] = useState(new Set());

  // Fetch all data once on page load
  const fetchAllData = async () => {
    const token = localStorage.getItem('token');
    try {
      // 1. Query all assignments where createdby == userData.account_id
      const assignmentsRes = await fetch('/api/assignments', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const assignmentsData = await assignmentsRes.json();
      const userAssignments = (assignmentsData.assignments || []).filter(a => 
        a.createdby === userData.account_id && a.reference_table !== 'components'
      );
      setMyAssignments(userAssignments);
      const assignmentIds = userAssignments.map(a => a.id);

      // 2. Query assignment submissions where .assignment has some of assignment_ids
      let allSubmissions = [];
      if (assignmentIds.length > 0) {
        const submissionsRes = await fetch('/api/assignmentsubmissions', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const submissionsData = await submissionsRes.json();
        allSubmissions = (submissionsData.submissions || submissionsData.assignmentSubmissions || [])
          .filter(s => assignmentIds.includes(s.assignment));
        setAssignmentSubmissions(allSubmissions);
      }

      // 3. Query all projects where createdby == userData.account_id
      const projectsRes = await fetch('/api/projects/my-projects', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const projectsData = await projectsRes.json();
      const userProjects = projectsData.projects || [];
      setMyProjects(userProjects);
      const projectIds = userProjects.map(p => p.id);

      // 4. Query project submissions where .project has some of project_ids
      let allProjectSubmissions = [];
      if (projectIds.length > 0) {
        const projectSubmissionsRes = await fetch('/api/projectsubmissions', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const projectSubmissionsData = await projectSubmissionsRes.json();
        allProjectSubmissions = (projectSubmissionsData.projectSubmissions || projectSubmissionsData.submissions || [])
          .filter(ps => projectIds.includes(ps.project));
        setProjectSubmissions(allProjectSubmissions);
      }

      // 5. Query all Evaluation comments for project submissions if any exist
      let allEvaluationComments = [];
      if (allProjectSubmissions.length > 0) {
        const projectSubmissionIds = allProjectSubmissions.map(ps => ps.id);
        const commentsRes = await fetch('/api/comments?table=projectsubmissions&type=Evaluation', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const commentsData = await commentsRes.json();
        allEvaluationComments = (commentsData.comments || [])
          .filter(c => projectSubmissionIds.includes(c.reference_id));
        setEvaluationComments(allEvaluationComments);
      }

      // 6. Query all Flag comments for projects if any exist
      let allFlagComments = [];
      if (projectIds.length > 0) {
        const flagCommentsRes = await fetch('/api/comments?table=projects&type=Flag', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const flagCommentsData = await flagCommentsRes.json();
        allFlagComments = (flagCommentsData.comments || [])
          .filter(c => projectIds.includes(c.reference_id));
        setFlagComments(allFlagComments);
      }

      // Process data in JavaScript
      const submissionsRequiringGrading = allSubmissions.filter(s => s.graderesult === 'needsEvaluation').length;

      // For project submissions waiting for evaluation, check which ones are:
      // 1. Concentration or Capstone type
      // 2. Status is 'started' (not complete)
      // 3. Missing Evaluation comments
      const concentrationCapstoneSubmissions = allProjectSubmissions.filter(ps => {
        const isConcentrationOrCapstone = ps.type === 'Concentration' || ps.type === 'Capstone';
        const isStarted = ps.status === 'started';
        return isConcentrationOrCapstone && isStarted;
      });

      let projectSubmissionsWaitingForEvaluation = 0;
      const evaluationRequiredSubmissionIds = new Set();

      for (const ps of concentrationCapstoneSubmissions) {
        // Check if there are any Evaluation comments for this submission
        const evaluationComments = allEvaluationComments.filter(c => c.reference_id === ps.id);
        
        // If no Evaluation comments exist, this submission is waiting for evaluation
        if (evaluationComments.length === 0) {
          projectSubmissionsWaitingForEvaluation++;
          evaluationRequiredSubmissionIds.add(ps.id);
        }
      }

      // For projects with unresolved flags, check Flag comments
      let projectsWithUnresolvedFlags = 0;
      const flaggedProjectIds = new Set();
      
      for (const project of userProjects) {
        const flagComments = allFlagComments.filter(c => c.reference_id === project.id);
        
        // Count unresolved flags (status is not "resolved")
        const unresolvedFlags = flagComments.filter(comment => comment.status !== 'resolved');
        
        if (unresolvedFlags.length > 0) {
          projectsWithUnresolvedFlags++;
          flaggedProjectIds.add(project.id);
        }
      }

      setCounts({
        assignments: userAssignments.length,
        submissions: allSubmissions.length,
        submissionsRequiringGrading,
        projects: userProjects.length,
        projectsWithUnresolvedFlags,
        projectSubmissions: allProjectSubmissions.length,
        projectSubmissionsWaitingForEvaluation
      });

      // Store the flagged projects and evaluation-required submissions for highlighting
      setFlaggedProjects(flaggedProjectIds);
      setEvaluationRequiredSubmissions(evaluationRequiredSubmissionIds);

      // Default to "needs attention" filters when there are any
      if (!hasSetDefaultFiltersRef.current) {
        hasSetDefaultFiltersRef.current = true;
        if (projectSubmissionsWaitingForEvaluation > 0) setProjectSubFilter('needsEvaluation');
        if (submissionsRequiringGrading > 0) setAssignmentSubFilter('requireGrading');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Remove the old useEffect that was re-fetching data on viewType change
  // All data is now loaded once on page load

  if (loading) return <LoadingSpinner />;
  if (error) return <div style={{ color: 'red' }}>{error}</div>;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <h1>My Directory</h1>

      {/* Navigation Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 16,
        marginBottom: 32
      }}>
        <div
          onClick={() => setViewType('assignments')}
          style={{
            padding: 20,
            border: '2px solid #e0e0e0',
            borderRadius: 12,
            background: viewType === 'assignments' ? '#f0f8ff' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: viewType === 'assignments' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
            borderColor: viewType === 'assignments' ? '#1976d2' : '#e0e0e0'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1976d2' }}>
            📝 My Assignments
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {counts.assignments} assignment{counts.assignments !== 1 ? 's' : ''}
          </div>
        </div>

        <div
          onClick={() => setViewType('submissions')}
          style={{
            padding: 20,
            border: '2px solid #e0e0e0',
            borderRadius: 12,
            background: viewType === 'submissions' ? '#fff3e0' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: viewType === 'submissions' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
            borderColor: viewType === 'submissions' ? '#f57c00' : '#e0e0e0'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#f57c00' }}>
            📋 Assignment Submissions
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {counts.submissions} total
            {counts.submissionsRequiringGrading > 0 && (
              <span style={{ color: '#d32f2f', fontWeight: 500, marginLeft: 8 }}>
                ({counts.submissionsRequiringGrading} Require Grading)
              </span>
            )}
          </div>
        </div>

        <div
          onClick={() => setViewType('projects')}
          style={{
            padding: 20,
            border: '2px solid #e0e0e0',
            borderRadius: 12,
            background: viewType === 'projects' ? '#e8f5e9' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: viewType === 'projects' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
            borderColor: viewType === 'projects' ? '#388e3c' : '#e0e0e0'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#388e3c' }}>
            🚩 My Projects
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {counts.projects} project{counts.projects !== 1 ? 's' : ''}
            {counts.projectsWithUnresolvedFlags > 0 && (
              <span style={{ color: '#d32f2f', fontWeight: 500, marginLeft: 8 }}>
                ({counts.projectsWithUnresolvedFlags} Unresolved Flags)
              </span>
            )}
          </div>
        </div>

        <div
          onClick={() => setViewType('projectSubmissions')}
          style={{
            padding: 20,
            border: '2px solid #e0e0e0',
            borderRadius: 12,
            background: viewType === 'projectSubmissions' ? '#f3e5f5' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            boxShadow: viewType === 'projectSubmissions' ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 4px rgba(0,0,0,0.05)',
            borderColor: viewType === 'projectSubmissions' ? '#7b1fa2' : '#e0e0e0'
          }}
          onMouseOver={e => e.currentTarget.style.transform = 'translateY(-2px)'}
          onMouseOut={e => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#7b1fa2' }}>
            📊 Project Submissions
          </div>
          <div style={{ fontSize: 14, color: '#666' }}>
            {counts.projectSubmissions} total
            {counts.projectSubmissionsWaitingForEvaluation > 0 && (
              <span style={{ color: '#f57c00', fontWeight: 500, marginLeft: 8 }}>
                ({counts.projectSubmissionsWaitingForEvaluation} Need Evaluation Comment)
              </span>
            )}
          </div>
        </div>
      </div>
      
      {viewType === 'assignments' ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 8 }}>Title</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Created</th>
              <th style={{ padding: 8 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {myAssignments.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 8 }}>{a.title}</td>
                <td style={{ padding: 8 }}>{a.createddate ? new Date(a.createddate).toLocaleString() : ''}</td>
                <td style={{ padding: 8 }}>
                  <div style={{ display: 'flex', gap: 8 }} key={`actions-${a.id}`}>
                    <button
                      onClick={() => window.open(`/assignment?id=${a.id}&mode=previewWithAnswers`, '_blank')}
                      style={{ padding: '6px 16px', borderRadius: 4, background: '#1976d2', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => window.open(`/assignment-builder?command=edit&id=${a.id}`, '_blank')}
                      style={{ padding: '6px 12px', borderRadius: 4, background: '#ff9800', color: 'white', border: 'none', cursor: 'pointer' }}
                      title="Edit Assignment"
                    >
                      ✏️Edit
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : viewType === 'projects' ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: 8 }}>Title</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Code</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Concentration</th>
              <th style={{ textAlign: 'left', padding: 8 }}>Created</th>
              <th style={{ padding: 8 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {myProjects.map(project => {
              const isFlagged = flaggedProjects.has(project.id);
              return (
                <tr key={project.id} style={{ 
                  borderBottom: '1px solid #eee',
                  background: isFlagged ? '#fff3f3' : 'transparent',
                  borderLeft: isFlagged ? '4px solid #d32f2f' : 'none'
                }}>
                  <td style={{ padding: 8 }}>
                    {project.title}
                    {isFlagged && (
                      <span style={{ 
                        color: '#d32f2f', 
                        fontWeight: 600, 
                        marginLeft: 8,
                        fontSize: '12px'
                      }}>
                        🚩 FLAGGED
                      </span>
                    )}
                  </td>
                  <td style={{ padding: 8 }}>{project.code}</td>
                  <td style={{ padding: 8 }}>{project.concentration}</td>
                  <td style={{ padding: 8 }}>{project.createddate ? new Date(project.createddate).toLocaleString() : ''}</td>
                  <td style={{ padding: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }} key={`project-actions-${project.id}`}>
                      <button
                        onClick={() => window.open(`/projects-directory?command=preview&project_code=${project.code}`, '_blank')}
                        style={{ padding: '6px 16px', borderRadius: 4, background: '#1976d2', color: 'white', border: 'none', cursor: 'pointer' }}
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => window.open(`/project-builder?command=edit&id=${project.id}`, '_blank')}
                        style={{ padding: '6px 12px', borderRadius: 4, background: '#ff9800', color: 'white', border: 'none', cursor: 'pointer' }}
                        title="Edit Project"
                      >
                        ✏️Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : viewType === 'projectSubmissions' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {counts.projectSubmissionsWaitingForEvaluation > 0 && (
            <div style={{
              padding: '12px 16px',
              marginBottom: 8,
              backgroundColor: '#fff8e1',
              border: '1px solid #ffc107',
              borderRadius: 8,
              color: '#5d4037',
              fontSize: 14
            }}>
              <strong>There are projects that need an evaluation comment.</strong> Despite students completing the assignments, they will not be awarded any hours until you add a comment on the project of type Evaluation.
            </div>
          )}
          {(() => {
            const psNeedsEval = projectSubmissions.filter(ps => evaluationRequiredSubmissions.has(ps.id)).length;
            const psInProgress = projectSubmissions.filter(ps => ps.status === 'started').length;
            const psCompleted = projectSubmissions.filter(ps => ps.status === 'complete').length;
            const psFiltered = projectSubmissions.filter(ps => {
              if (projectSubFilter === 'needsEvaluation') return evaluationRequiredSubmissions.has(ps.id);
              if (projectSubFilter === 'inProgress') return ps.status === 'started';
              if (projectSubFilter === 'completed') return ps.status === 'complete';
              return true;
            });
            return (
              <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontWeight: 500, color: '#555', fontSize: 14 }}>Filters:</span>
            <button
              onClick={() => setProjectSubFilter('all')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: projectSubFilter === 'all' ? '2px solid #666' : '1px solid #ccc',
                background: projectSubFilter === 'all' ? '#f5f5f5' : '#fff',
                color: projectSubFilter === 'all' ? '#333' : '#666',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: projectSubFilter === 'all' ? 600 : 400
              }}
            >
              All ({projectSubmissions.length})
            </button>
            <button
              onClick={() => setProjectSubFilter(projectSubFilter === 'needsEvaluation' ? 'all' : 'needsEvaluation')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: projectSubFilter === 'needsEvaluation' ? '2px solid #f57c00' : '1px solid #ccc',
                background: projectSubFilter === 'needsEvaluation' ? '#fff3e0' : '#fff',
                color: projectSubFilter === 'needsEvaluation' ? '#e65100' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: projectSubFilter === 'needsEvaluation' ? 600 : 400
              }}
            >
              Filter to Those that need Evaluation comment ({psNeedsEval})
            </button>
            <button
              onClick={() => setProjectSubFilter(projectSubFilter === 'inProgress' ? 'all' : 'inProgress')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: projectSubFilter === 'inProgress' ? '2px solid #f57c00' : '1px solid #ccc',
                background: projectSubFilter === 'inProgress' ? '#fff3e0' : '#fff',
                color: projectSubFilter === 'inProgress' ? '#e65100' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: projectSubFilter === 'inProgress' ? 600 : 400
              }}
            >
              In Progress ({psInProgress})
            </button>
            <button
              onClick={() => setProjectSubFilter(projectSubFilter === 'completed' ? 'all' : 'completed')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: projectSubFilter === 'completed' ? '2px solid #388e3c' : '1px solid #ccc',
                background: projectSubFilter === 'completed' ? '#e8f5e9' : '#fff',
                color: projectSubFilter === 'completed' ? '#2e7d32' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: projectSubFilter === 'completed' ? 600 : 400
              }}
            >
              Completed ({psCompleted})
            </button>
            <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>
              Showing {psFiltered.length}
            </span>
          </div>
          {psFiltered.map((ps, index) => {
            let statusColor = '#333';
            let statusText = ps.status || 'Unknown';
            const needsEvaluation = evaluationRequiredSubmissions.has(ps.id);

            if (ps.status === 'complete') {
              statusColor = '#388e3c'; // green
              statusText = 'Completed';
            } else if (ps.status === 'started') {
              statusColor = '#f57c00'; // orange
              statusText = 'In Progress';
            }

            return (
              <div key={ps.id} style={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                border: needsEvaluation ? '2px solid #f57c00' : '1px solid #e0e0e0', 
                borderRadius: 8, 
                padding: 16, 
                background: needsEvaluation ? '#fff8e1' : '#fafbfc', 
                boxShadow: needsEvaluation ? '0 2px 8px rgba(245,124,0,0.2)' : '0 1px 2px rgba(0,0,0,0.03)',
                position: 'relative'
              }}>
                {needsEvaluation && (
                  <div style={{
                    position: 'absolute',
                    top: -8,
                    right: 16,
                    background: '#f57c00',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}>
                    Evaluation Comment Required
                  </div>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', minWidth: 28, marginRight: 8 }}>{index + 1}.</span>
                <div style={{ marginRight: 16, marginTop: 2, cursor: 'pointer', transition: 'transform 0.1s', display: 'flex', alignItems: 'center' }}
                  title="View Project Submission"
                  onClick={() => window.open(`/projects-directory?command=evaluate&projectSubmission=${ps.id}`, '_blank')}
                  onMouseOver={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span role="img" aria-label="project" style={{ fontSize: 28, color: '#4caf50' }}>📋</span>
                </div>
                {/* Main content */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2, color: '#222' }}>
                    {ps.projecttitle || 'Untitled Project'}
                  </div>
                  <div style={{ fontSize: 14, marginBottom: 4 }}>
                    <span style={{ color: statusColor, fontWeight: 500 }}>Status: {statusText}</span>
                    {ps.type && (
                      <span style={{ color: '#555', marginLeft: 8, fontSize: 13 }}>
                        (Type: {ps.type})
                      </span>
                    )}
                    {ps.hoursreceived && (
                      <span style={{ color: '#11AF07', marginLeft: 8, fontSize: 13, fontWeight: 500 }}>
                        ({ps.hoursreceived} hours received)
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#666' }}>
                    Student: {ps.studentname || 'Unknown Student'}
                  </div>
                </div>
                {/* Dates */}
                <div style={{ textAlign: 'right', minWidth: 180 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Started: {ps.createddate ? new Date(ps.createddate).toLocaleString() : ''}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>Updated: {ps.updateddate ? new Date(ps.updateddate).toLocaleString() : ''}</div>
                </div>
              </div>
            );
          })}
              </>
            );
          })()}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {counts.submissionsRequiringGrading > 0 && (
            <div style={{
              padding: '12px 16px',
              marginBottom: 8,
              backgroundColor: '#ffebee',
              border: '1px solid #ef5350',
              borderRadius: 8,
              color: '#b71c1c',
              fontSize: 14
            }}>
              <strong>There are assignments that require grading.</strong> They have questions like upload or short answers that must be assigned points to complete assessment-type assignments. (Surveys do not need grading regardless of question type.)
            </div>
          )}
          {(() => {
            const asRequireGrading = assignmentSubmissions.filter(s => s.graderesult === 'needsEvaluation').length;
            const asSurvey = assignmentSubmissions.filter(s => s.graderesult === 'not-required').length;
            const asGraded = assignmentSubmissions.filter(s => s.graderesult !== 'not-required').length;
            const asFiltered = assignmentSubmissions.filter(s => {
              if (assignmentSubFilter === 'requireGrading') return s.graderesult === 'needsEvaluation';
              if (assignmentSubFilter === 'survey') return s.graderesult === 'not-required';
              if (assignmentSubFilter === 'graded') return s.graderesult !== 'not-required';
              return true;
            });
            return (
              <>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontWeight: 500, color: '#555', fontSize: 14 }}>Filters:</span>
            <button
              onClick={() => setAssignmentSubFilter('all')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: assignmentSubFilter === 'all' ? '2px solid #666' : '1px solid #ccc',
                background: assignmentSubFilter === 'all' ? '#f5f5f5' : '#fff',
                color: assignmentSubFilter === 'all' ? '#333' : '#666',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: assignmentSubFilter === 'all' ? 600 : 400
              }}
            >
              All ({assignmentSubmissions.length})
            </button>
            <button
              onClick={() => setAssignmentSubFilter(assignmentSubFilter === 'requireGrading' ? 'all' : 'requireGrading')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: assignmentSubFilter === 'requireGrading' ? '2px solid #d32f2f' : '1px solid #ccc',
                background: assignmentSubFilter === 'requireGrading' ? '#ffebee' : '#fff',
                color: assignmentSubFilter === 'requireGrading' ? '#b71c1c' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: assignmentSubFilter === 'requireGrading' ? 600 : 400
              }}
            >
              Filter to assignments that Require Grading ({asRequireGrading})
            </button>
            <button
              onClick={() => setAssignmentSubFilter(assignmentSubFilter === 'survey' ? 'all' : 'survey')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: assignmentSubFilter === 'survey' ? '2px solid #1976d2' : '1px solid #ccc',
                background: assignmentSubFilter === 'survey' ? '#e3f2fd' : '#fff',
                color: assignmentSubFilter === 'survey' ? '#0d47a1' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: assignmentSubFilter === 'survey' ? 600 : 400
              }}
            >
              Survey Assignments ({asSurvey})
            </button>
            <button
              onClick={() => setAssignmentSubFilter(assignmentSubFilter === 'graded' ? 'all' : 'graded')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: assignmentSubFilter === 'graded' ? '2px solid #388e3c' : '1px solid #ccc',
                background: assignmentSubFilter === 'graded' ? '#e8f5e9' : '#fff',
                color: assignmentSubFilter === 'graded' ? '#2e7d32' : '#333',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: assignmentSubFilter === 'graded' ? 600 : 400
              }}
            >
              Graded Assignments ({asGraded})
            </button>
            <span style={{ fontSize: 13, color: '#888', marginLeft: 8 }}>
              Showing {asFiltered.length}
            </span>
          </div>
          {asFiltered.map((s, index) => {
            let notice = '';
            let noticeColor = '#333';
            const studentName = s.emaildata && typeof s.emaildata === 'string'
              ? (() => { try { return JSON.parse(s.emaildata).studentName || 'Student'; } catch { return 'Student'; } })()
              : (s.emaildata?.studentName || 'Student');
            if (s.graderesult === 'needsEvaluation') {
              notice = `REQUIRES GRADING for ${studentName}`;
              noticeColor = '#d32f2f'; // red
            } else if (s.graderesult === 'not-required') {
              notice = `SURVEY--NO GRADING REQUIRED for ${studentName}`;
              noticeColor = '#1976d2'; // blue
            } else if (typeof s.graderesult === 'string' && s.graderesult.includes('%')) {
              notice = `${s.graderesult} for ${studentName}`;
              noticeColor = '#388e3c'; // green
            } else {
              notice = `Status: ${s.graderesult || 'N/A'} for ${studentName}`;
              noticeColor = '#333';
            }
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'flex-start', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, background: '#fafbfc', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#94a3b8', minWidth: 28, marginRight: 8 }}>{index + 1}.</span>
                <div style={{ marginRight: 16, marginTop: 2, cursor: 'pointer', transition: 'transform 0.1s', display: 'flex', alignItems: 'center' }}
                  title="View Submission"
                  onClick={() => window.open(`/projects-directory?command=grade&assignmentSubmission=${s.id}`, '_blank')}
                  onMouseOver={e => e.currentTarget.style.transform = 'scale(1.15)'}
                  onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <span role="img" aria-label="assignment" style={{ fontSize: 28, color: '#fbc02d' }}>📝</span>
                </div>
                {/* Main content */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 2, color: '#222' }}>{s.assignmenttitle || s.title || 'Untitled'}</div>
                  <div style={{ fontSize: 14, marginBottom: 4 }}>
                    <span style={{ color: noticeColor, fontWeight: 500 }}>{notice}</span>
                    {s.projecttitle && (
                      <span style={{ color: '#555', marginLeft: 8, fontSize: 13 }}>
                        (P: {s.projecttitle})
                      </span>
                    )}
                  </div>
                </div>
                {/* Dates */}
                <div style={{ textAlign: 'right', minWidth: 180 }}>
                  <div style={{ fontSize: 12, color: '#666' }}>Created : {s.createddate ? new Date(s.createddate).toLocaleString() : ''}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>Updated : {s.updateddate ? new Date(s.updateddate).toLocaleString() : ''}</div>
                </div>
              </div>
            );
          })}
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default MyDirectory; 